import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { usePageTitle } from "@/lib/usePageTitle";
import { TRADE_CATS, TRADES } from "@/lib/trades";

export default function BookJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const serviceId = new URLSearchParams(loc.search).get("service");
  const tradeParam = new URLSearchParams(loc.search).get("trade");
  const preTrade = TRADES.find((t) => t.category === tradeParam);

  const [service, setService] = useState(null);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: preTrade?.category || "", urgency: "medium", address: "", preferred_date: "" });
  usePageTitle("Book a job — Olivia Does");

  useEffect(() => {
    if (serviceId) {
      api.get("/services").then((r) => {
        const s = r.data.find((x) => x.id === serviceId);
        if (s) { setService(s); setForm((f) => ({ ...f, title: s.name, category: s.category, description: s.description })); }
      });
    }
  }, [serviceId]);

  const runAI = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/classify-job", { description: aiInput });
      setAiResult(data);
      setForm((f) => ({ ...f, title: data.suggested_service, description: data.refined_description, category: data.trade_category, urgency: data.urgency }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Olivia's concierge is unavailable right now");
    } finally { setAiLoading(false); }
  };

  const submitJob = async () => {
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/book${loc.search}`)}`); return; }
    setBusy(true);
    try {
      const payload = { ...form, job_type: service?.type === "instant" ? "instant" : "quote", service_id: service?.id || null, price: service?.price ?? null, photos: [] };
      const { data } = await api.post("/jobs", payload);
      toast.success("Job submitted. Olivia will be in touch.");
      navigate(`/jobs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit job");
    } finally { setBusy(false); }
  };

  const chips = [
    ["Leaking tap in the kitchen", "Leaking tap in the kitchen, worse over the last few days."],
    ["Repaint the lounge", "Would like the lounge repainted, roughly 4x5m room."],
    ["Garden's out of control", "Garden has gotten out of control, needs a full tidy."],
  ];

  return (
    <section className="od-section od-wrap" style={{ maxWidth: 760 }} data-testid="book-page">
      <div className="od-eyebrow">Book a job</div>
      <h1 className="serif" style={{ fontSize: "2.2rem", fontWeight: 500, margin: "0 0 10px" }} data-testid="book-heading">{service ? service.name : preTrade ? `${preTrade.name} — tell Olivia what's needed.` : "Tell Olivia what's going on."}</h1>
      <p style={{ color: "var(--taupe)", marginBottom: 28 }}>
        {service ? `${service.description} · ${service.price != null ? `£${service.price}` : "By quote"}` : preTrade ? `We've set the trade to ${preTrade.category} for you. Describe the job in plain English and we'll take it from there.` : "Describe it in plain English — our concierge sorts the trade, urgency and next step."}
      </p>

      {!service && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="od-eyebrow" style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Sparkles className="icon" style={{ width: 16, height: 16 }} /> Olivia concierge</div>
          <textarea className="finput" data-testid="ai-input" value={aiInput} onChange={(e) => setAiInput(e.target.value)} placeholder="e.g. My kitchen tap has been dripping for a few days and it's getting worse..." />
          <div className="chiprow">
            {chips.map(([label, text]) => (
              <div key={label} className="chip" onClick={() => setAiInput(text)}>{label}</div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <button className="btn primary" data-testid="ai-submit" onClick={runAI} disabled={aiLoading || !aiInput.trim()}>
              {aiLoading ? <><Loader2 className="icon" style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} /> Olivia is thinking…</> : "Ask Olivia →"}
            </button>
          </div>
          {aiResult && (
            <div className="quotebox" style={{ display: "block" }} data-testid="ai-result">
              <div className="od-eyebrow" style={{ marginBottom: 8 }}>Here's what Olivia heard</div>
              <p style={{ margin: 0, color: "var(--taupe)", fontSize: ".9rem" }}><b style={{ color: "var(--charcoal)" }}>{aiResult.trade_category}</b> · {aiResult.urgency} · est. {aiResult.estimated_visit_minutes} mins</p>
              <p style={{ marginTop: 8, marginBottom: 0 }}>{aiResult.refined_description}</p>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="ogrid cols-2" style={{ gap: 16 }}>
          <div className="field"><label className="flabel">Title</label><input className="finput" data-testid="job-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short summary" /></div>
          <div className="field"><label className="flabel">Trade</label>
            <select className="finput" data-testid="job-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="">Select trade</option>
              {TRADE_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field" style={{ gridColumn: "1/3" }}><label className="flabel">Description</label><textarea className="finput" data-testid="job-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="field"><label className="flabel">Urgency</label>
            <select className="finput" data-testid="job-urgency" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
              <option value="low">Standard (within a week)</option>
              <option value="medium">Soon (few days)</option>
              <option value="high">Urgent (within 48 hrs)</option>
              <option value="emergency">Emergency (today)</option>
            </select>
          </div>
          <div className="field"><label className="flabel">Preferred date</label><input className="finput" data-testid="job-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></div>
          <div className="field" style={{ gridColumn: "1/3" }}><label className="flabel">Property address</label><input className="finput" data-testid="job-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House number, street, postcode" /></div>
        </div>
        <button className="btn rose block" data-testid="job-submit" onClick={submitJob} disabled={busy || (!!user && (!form.title || !form.address || !form.category))}>
          {busy ? "Submitting…" : !user ? "Sign in to confirm booking" : service?.type === "instant" ? `Confirm booking · £${service.price}` : "Request quote"}
        </button>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </section>
  );
}
